
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    Paper,
    Grid,
    Card,
    CardContent,
    CircularProgress,
    Tooltip,
    alpha,
    useTheme,
    Breadcrumbs,
    Link,
    Avatar,
    Divider,
    Chip,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    ArrowBack as ArrowBackIcon,
    Folder as FolderIcon,
    CloudUpload as CloudUploadIcon,
    Phone as PhoneIcon,
    LocationOn as LocationOnIcon,
    Close as CloseIcon,
    Save as SaveIcon,
    Warning as WarningIcon,
    Search as SearchIcon,
    MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { directoryService } from '../service/directoryService';
import { getImageUrl } from '../service/api';
import SuccessNotification from '../components/SuccessNotification';

export default function Directory() {
    const theme = useTheme();
    const [groups, setGroups] = useState([]);
    const [schemes, setSchemes] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [loading, setLoading] = useState(true);

    // Group Modal
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [isEditingGroup, setIsEditingGroup] = useState(false);
    const [groupFormData, setGroupFormData] = useState({ name: '', category: 'Contact', icon: null });
    const [groupIconPreview, setGroupIconPreview] = useState(null);

    // Scheme Modal
    const [schemeModalOpen, setSchemeModalOpen] = useState(false);
    const [isEditingScheme, setIsEditingScheme] = useState(false);
    const [selectedSchemeId, setSelectedSchemeId] = useState(null);
    const [schemeFormData, setSchemeFormData] = useState({
        title: '',
        description: '',
        mobile: '',
        location: '',
        locationLink: '',
        redirectionLink: ''
    });

    // Delete Confirmation
    const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Pagination & Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        fetchGroups();
    }, []);

    const fetchGroups = async () => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second
            const [response] = await Promise.all([
                directoryService.getDirectoryGroups(),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
            if (response.success) setGroups(response.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const fetchItemsForGroup = async (groupId) => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second
            const [response] = await Promise.all([
                directoryService.getDirectorySchemes(groupId),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
            if (response.success) setSchemes(response.data || []);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleGroupClick = (group) => {
        setSelectedGroup(group);
        setPage(0);
        setSearchTerm('');
        fetchItemsForGroup(group.id);
    };

    const handleBack = () => {
        setSelectedGroup(null);
        setSchemes([]);
        setPage(0);
        setSearchTerm('');
    };

    const handleOpenGroupModal = (group = null) => {
        if (group) {
            setIsEditingGroup(true);
            setGroupFormData({ name: group.groupName, category: group.category || 'Contact', icon: null });
            setGroupIconPreview(group.groupIcon);
        } else {
            setIsEditingGroup(false);
            setGroupFormData({ name: '', category: 'Contact', icon: null });
            setGroupIconPreview(null);
        }
        setGroupModalOpen(true);
    };

    const handleIconChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGroupFormData({ ...groupFormData, icon: file });
            setGroupIconPreview(URL.createObjectURL(file));
        }
    };

    const handleGroupSubmit = async () => {
        if (!groupFormData.name.trim()) return;
        setLoading(true);
        try {
            let response;
            if (isEditingGroup) {
                response = await directoryService.updateDirectoryGroup(selectedGroup.id, groupFormData.name, groupFormData.category, groupFormData.icon);
            } else {
                response = await directoryService.addDirectoryGroup(groupFormData.name, groupFormData.category, groupFormData.icon);
            }
            if (response.success) {
                fetchGroups();
                setGroupModalOpen(false);
                setNotification({ open: true, message: 'Group saved successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSchemeModal = (scheme = null) => {
        if (scheme) {
            setIsEditingScheme(true);
            setSelectedSchemeId(scheme.id);
            setSchemeFormData({
                title: scheme.title || '',
                description: scheme.description || '',
                mobile: scheme.mobile || '',
                location: scheme.location || '',
                locationLink: scheme.locationLink || '',
                redirectionLink: scheme.redirectionLink || ''
            });
        } else {
            setIsEditingScheme(false);
            setSchemeFormData({ title: '', description: '', mobile: '', location: '', locationLink: '', redirectionLink: '' });
        }
        setSchemeModalOpen(true);
    };

    const handleSchemeSubmit = async () => {
        if (!schemeFormData.title.trim()) return;
        setLoading(true);
        try {
            let response;
            if (isEditingScheme) {
                response = await directoryService.updateDirectoryScheme(selectedSchemeId, selectedGroup.id, schemeFormData.title, schemeFormData.description, schemeFormData.redirectionLink, schemeFormData.locationLink, schemeFormData.mobile, schemeFormData.location);
            } else {
                response = await directoryService.addDirectoryScheme(selectedGroup.id, schemeFormData.title, schemeFormData.description, schemeFormData.redirectionLink, schemeFormData.locationLink, schemeFormData.mobile, schemeFormData.location);
            }
            if (response.success) {
                fetchItemsForGroup(selectedGroup.id);
                setSchemeModalOpen(false);
                setNotification({ open: true, message: 'Item saved successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = async () => {
        const { type, id } = deleteDialog;
        setDeleteDialog({ ...deleteDialog, open: false });
        setLoading(true);
        try {
            let response = type === 'group' ? await directoryService.deleteDirectoryGroup(id) : await directoryService.deleteDirectoryScheme(id);
            if (response.success) {
                type === 'group' ? fetchGroups() : fetchItemsForGroup(selectedGroup.id);
                setNotification({ open: true, message: 'Deleted successfully!', severity: 'success' });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ pb: 5 }}>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)',
                gap: 2
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                        {selectedGroup ? selectedGroup.groupName : 'Directory'}
                    </Typography>
                    <Breadcrumbs sx={{ mt: 0.5, '& .MuiBreadcrumbs-li': { fontSize: { xs: '0.75rem', sm: '0.85rem' } } }}>
                        <Link
                            component="button"
                            onClick={handleBack}
                            underline="hover"
                            sx={{ color: '#64748b', fontWeight: 600 }}
                        >
                            Directory
                        </Link>
                        {selectedGroup && (
                            <Typography sx={{ color: 'primary.main', fontWeight: 700 }}>
                                {selectedGroup.groupName}
                            </Typography>
                        )}
                    </Breadcrumbs>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={selectedGroup ? () => handleOpenSchemeModal() : () => handleOpenGroupModal()}
                    sx={{
                        borderRadius: 2.5,
                        px: 3,
                        py: 1.2,
                        width: { xs: '100%', sm: 'auto' },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        background: 'primary.main',
                        boxShadow: `0 4px 14px 0 ${alpha(theme.palette.secondary.main, 0.39)}`,
                        '&:hover': {
                            background: 'primary.dark',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.23)}`
                        }
                    }}
                >
                    {selectedGroup ? 'Add Item' : 'Add Group'}
                </Button>
            </Box>

            <Paper sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                mb: 4
            }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        placeholder={selectedGroup ? "Search items by title, location or mobile..." : "Search groups by name or category..."}
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: '#f8fafc',
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover': { bgcolor: '#f1f5f9' },
                                transition: 'all 0.2s'
                            }
                        }}
                    />
                </Box>

                {!selectedGroup ? (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Icon</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Group Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Category</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Items Count</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from(new Array(rowsPerPage)).map((_, index) => (
                                        <TableRow key={`skeleton-group-${index}`}>
                                            <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                            <TableCell><Skeleton variant="circular" width={40} height={40} /></TableCell>
                                            <TableCell><Skeleton width="60%" /></TableCell>
                                            <TableCell><Skeleton width="40%" height={24} sx={{ borderRadius: 1.5 }} /></TableCell>
                                            <TableCell><Skeleton width="30%" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : groups
                                    .filter(group =>
                                        group.groupName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        group.category?.toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((group) => (
                                        <TableRow
                                            key={group.id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                transition: 'all 0.2s'
                                            }}
                                            onClick={() => handleGroupClick(group)}
                                        >
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenGroupModal(group)}
                                                        sx={{ color: '#64748b' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteDialog({ open: true, type: 'group', id: group.id })}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Avatar
                                                    src={group.groupIcon}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        border: '1px solid #e2e8f0'
                                                    }}
                                                >
                                                    <FolderIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                                                </Avatar>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{group.groupName}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={group.category}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        color: theme.palette.primary.main,
                                                        textTransform: 'capitalize'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 600, color: '#64748b' }}>{group.count || 0}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={groups.filter(group =>
                                group.groupName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                group.category?.toLowerCase().includes(searchTerm.toLowerCase())
                            ).length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </TableContainer>
                ) : (
                    <TableContainer sx={{ overflowX: 'auto' }}>
                        <Table sx={{ minWidth: 800 }}>
                            <TableHead sx={{ bgcolor: '#f8fafc' }}>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Icon</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Title/Name</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Mobile</TableCell>
                                    <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Location</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    Array.from(new Array(rowsPerPage)).map((_, index) => (
                                        <TableRow key={`skeleton-item-${index}`}>
                                            <TableCell><Skeleton variant="circular" width={24} height={24} /></TableCell>
                                            <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                            <TableCell><Skeleton width="70%" /></TableCell>
                                            <TableCell><Skeleton width="50%" /></TableCell>
                                            <TableCell><Skeleton width="60%" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : schemes
                                    .filter(item =>
                                        item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        item.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                        item.location?.toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((item) => (
                                        <TableRow
                                            key={item.id}
                                            hover
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1 }}>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleOpenSchemeModal(item)}
                                                        sx={{ color: '#64748b' }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setDeleteDialog({ open: true, type: 'scheme', id: item.id })}
                                                        color="error"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Avatar sx={{
                                                    width: 32,
                                                    height: 32,
                                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                    color: theme.palette.primary.main,
                                                    fontWeight: 700,
                                                    fontSize: '0.8rem'
                                                }}>
                                                    {item.title?.charAt(0).toUpperCase()}
                                                </Avatar>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{item.title}</TableCell>
                                            <TableCell sx={{ color: '#64748b', fontWeight: 500 }}>{item.mobile || '-'}</TableCell>
                                            <TableCell sx={{ color: '#64748b' }}>{item.location || '-'}</TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={schemes.filter(item =>
                                item.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                item.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                item.location?.toLowerCase().includes(searchTerm.toLowerCase())
                            ).length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={(e, p) => setPage(p)}
                            onRowsPerPageChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(0);
                            }}
                        />
                    </TableContainer>
                )}
            </Paper>

            <Dialog open={groupModalOpen} onClose={() => setGroupModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{isEditingGroup ? 'Edit Group' : 'Add Group'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <TextField label="Group Name" fullWidth value={groupFormData.name} onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })} />
                        <TextField select label="Category" fullWidth value={groupFormData.category} onChange={(e) => setGroupFormData({ ...groupFormData, category: e.target.value })} SelectProps={{ native: true }} variant="outlined">
                            <option value="Contact">Contact</option>
                            <option value="Scheme">Scheme</option>
                        </TextField>
                        <Box>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
                                Group Icon
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar
                                    src={groupIconPreview}
                                    variant="rounded"
                                    sx={{ width: 80, height: 80, bgcolor: '#f8fafc', border: '1px dashed #e2e8f0' }}
                                >
                                    <FolderIcon sx={{ fontSize: 32, color: '#94a3b8' }} />
                                </Avatar>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    startIcon={<CloudUploadIcon />}
                                    sx={{ textTransform: 'none', borderRadius: 2 }}
                                >
                                    Upload Icon
                                    <input type="file" hidden accept="image/*" onChange={handleIconChange} />
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setGroupModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleGroupSubmit}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={schemeModalOpen} onClose={() => setSchemeModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{isEditingScheme ? 'Edit' : 'Add'} Item</DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 1, pt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField label="Name/Title" fullWidth value={schemeFormData.title} onChange={(e) => setSchemeFormData({ ...schemeFormData, title: e.target.value })} />
                        <TextField label="Mobile" fullWidth value={schemeFormData.mobile} onChange={(e) => setSchemeFormData({ ...schemeFormData, mobile: e.target.value })} />
                        <TextField label="Location" fullWidth value={schemeFormData.location} onChange={(e) => setSchemeFormData({ ...schemeFormData, location: e.target.value })} />
                        <TextField label="Location Link" fullWidth value={schemeFormData.locationLink} onChange={(e) => setSchemeFormData({ ...schemeFormData, locationLink: e.target.value })} />
                        <TextField label="Redirection Link" fullWidth value={schemeFormData.redirectionLink} onChange={(e) => setSchemeFormData({ ...schemeFormData, redirectionLink: e.target.value })} />
                        <TextField label="Description" fullWidth multiline rows={3} value={schemeFormData.description} onChange={(e) => setSchemeFormData({ ...schemeFormData, description: e.target.value })} />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setSchemeModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSchemeSubmit}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ ...deleteDialog, open: false })}>
                <DialogContent sx={{ py: 4, textAlign: 'center' }}>
                    <WarningIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
                    <Typography variant="h6">Confirm Delete?</Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
                    <Button onClick={() => setDeleteDialog({ ...deleteDialog, open: false })}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            <SuccessNotification open={notification.open} message={notification.message} onClose={() => setNotification({ ...notification, open: false })} />
        </Box>
    );
}

